// Compress a photo in the browser before upload.
// Target: longest side <= 1600px, JPEG ~70% quality, <= ~500 KB.
// Returns { dataUrl, size, type, name, compressed } or throws if the image cannot be read.

const MAX_SIDE = 1600;
const TARGET_BYTES = 500 * 1024;

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Cannot read image")); };
    img.src = url; // modern browsers apply EXIF rotation when drawing
  });
}

function toBlob(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Cannot read compressed image"));
    r.readAsDataURL(blob);
  });
}

export function isCompressibleImage(file) {
  const t = (file && file.type) || "";
  // GIF (animation) and SVG (vector) are left alone
  return t.startsWith("image/") && t !== "image/gif" && t !== "image/svg+xml";
}

export async function compressImage(file) {
  const img = await loadImage(file);
  let w = img.naturalWidth || img.width;
  let h = img.naturalHeight || img.height;
  if (!w || !h) throw new Error("Cannot read image");
  const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
  w = Math.round(w * scale);
  h = Math.round(h * scale);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  let blob = null;
  // Try quality steps first, then shrink the size if still above target
  for (let attempt = 0; attempt < 4; attempt++) {
    canvas.width = w;
    canvas.height = h;
    ctx.fillStyle = "#ffffff"; // transparent PNG areas become white, not black
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    for (const q of [0.7, 0.6, 0.5]) {
      blob = await toBlob(canvas, q);
      if (blob && blob.size <= TARGET_BYTES) break;
    }
    if (blob && blob.size <= TARGET_BYTES) break;
    w = Math.round(w * 0.8);
    h = Math.round(h * 0.8);
  }
  if (!blob) throw new Error("Cannot compress image");

  // Never make a small file bigger
  if (file.size <= blob.size && file.size <= TARGET_BYTES && file.type === "image/jpeg") {
    return { dataUrl: await blobToDataUrl(file), size: file.size, type: file.type, name: file.name, compressed: false };
  }
  const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
  return { dataUrl: await blobToDataUrl(blob), size: blob.size, type: "image/jpeg", name, compressed: true };
}
